// Send-side loudness AGC.
//
// Inserted in the mic graph just BEFORE the published MediaStreamDestination
// (in both the DFN and RNNoise denoiser graphs). Levels the OUTGOING mic
// toward a common target so every participant arrives at a consistent
// loudness — the Discord model: per-user AGC at the SOURCE.
//
// Why send-side and not receive-side: we previously normalized on the RECEIVE
// side (boosting each peer's playback). Boosting playback pushed more sound
// into everyone's mics → acoustic ECHO across the call. Doing it on the SEND
// side is echo-safe — this is the local OUTGOING signal; the talker doesn't
// hear their own mic, and others hearing them louder is just normal
// cross-talk their AEC already handles. No feedback loop.
//
// Wiring (caller):
//   denoiserNode.connect(agc.input)
//   agc.output.connect(destination)
//
// Feedforward control: measure the pre-makeup level, set makeup = target/level
// (clamped + slewed). Quiet talkers up, loud talkers down. Gated to speech so
// we don't amplify silence/residual noise. Toggle: localStorage 'voiceSendAgc'
// (default on).
//
// Loudness measure: flat RMS by default, or perceptual K-weighted (LUFS / BS.1770)
// via localStorage 'voiceSendAgcMode'='lufs' (target 'voiceSendAgcTargetDb'). Both
// levels are logged ('[sendagc]') every ~2 s regardless of mode so the LUFS target
// can be calibrated from real speech before it becomes the default. See the
// K-weighting constants + measurement branch below.

export interface SendAgc {
  /** Connect the denoiser output node here. */
  input: AudioNode
  /** Connect this to the published MediaStreamDestination. */
  output: AudioNode
  destroy: () => void
}

const TARGET_RMS = 0.1 // ≈ -20 dBFS — consistent outgoing speech level
const SPEECH_FLOOR = 0.01 // below this = silence/noise → hold (don't adapt)
const MIN_GAIN = 0.125 // ≈ -18 dB — deepened from -12 dB: a very-hot mic ("Леха") still sat above everyone at -12 dB (his raw is so loud that 0.25×raw > TARGET). -18 dB lets the SOURCE AGC pull even clipping-hot mics to the common level. Only affects talkers hot enough to hit the floor; normal/quiet talkers' desired gain is already > this, so they're untouched. Echo-safe (it's the outgoing source).
const MAX_GAIN = 8.0 // ≈ +18 dB — raised from +12 dB. Prod ClickHouse (loudnessDbfs, 2026-05-21) showed active speakers ARRIVING at -24..-33 dBFS (well below the -20 target): the denoiser (DFN/RNNoise) eats several dB and quiet talkers (Sergey -33) hit the old +12 cap, so they couldn't reach the common level. +18 dB gives the source AGC the headroom to lift them. Only affects talkers being lifted (quiet ones); a talker already at/above target isn't touched, so it does NOT make a loud talker louder. Echo-safe (after the denoiser, at the source → low noise to amplify).
const TIME_CONSTANT = 0.4 // setTargetAtTime smoothing, seconds (anti-pump)
const INTERVAL_MS = 200

// --- Perceptual (LUFS / ITU-R BS.1770 K-weighting) leveling ------------------
// Flat RMS targets raw waveform energy, which is NOT perceived loudness: the ear
// under-weights bass and peaks ~2-5 kHz (equal-loudness contours). Two voices
// RMS-leveled to the same -20 dBFS still sound uneven if their timbre differs (a
// bassy desk mic spends energy the ear discounts → ends up perceptually quieter
// than a bright headset at the same RMS). K-weighting (the BS.1770 pre-filter
// behind LUFS / EBU R128 / streaming loudness) shapes the signal to match the ear
// BEFORE measuring energy → equal measurement == equal perceived loudness.
//
// We measure the K-weighted level on a MEASUREMENT-ONLY branch (a leaf off `tee`,
// never in the published path) in parallel with flat RMS; the control loop picks
// which drives the makeup via localStorage 'voiceSendAgcMode' ('rms' | 'lufs').
// DEFAULT 'rms' — the absolute K-weighted target must be calibrated from real
// speech first (we log BOTH levels every ~2 s so the per-talker offset is visible);
// flip the default to 'lufs' once the target is dialed in. Echo-safety, clamps,
// slew and cross-rebuild persistence are all unchanged — only the measured number.
//
// ITU-R BS.1770-4 K-weighting biquads @ 48 kHz (a0 normalised to 1): stage 1 =
// "pre-filter" head high-shelf (+4 dB, ~1.5 kHz), stage 2 = "RLB" high-pass (rolls
// off sub-bass the ear ignores). 48 kHz is guaranteed for every send graph
// (invariant #9); we still guard ctx.sampleRate and fall back to flat RMS otherwise.
const KW_PREFILTER_B = [1.53512485958697, -2.69169618940638, 1.19839281085285]
const KW_PREFILTER_A = [1.0, -1.69065929318241, 0.73248077421585]
const KW_RLB_B = [1.0, -2.0, 1.0]
const KW_RLB_A = [1.0, -1.99004745483398, 0.99007225036621]
// Target for the K-weighted measure (same "dBFS of the weighted signal" domain as
// the measurement). Starting estimate ≈ the flat -20 dBFS; CALIBRATE live from the
// '[sendagc]' dual log, override via localStorage 'voiceSendAgcTargetDb'.
const TARGET_LUFS_DBFS_DEFAULT = -20
const TARGET_LUFS_LIN_DEFAULT = Math.pow(10, TARGET_LUFS_DBFS_DEFAULT / 20)

// Persisted across graph rebuilds (denoiser cascade DFN↔RNNoise, health-check
// re-apply, device switch, mute/unmute). Each rebuild makes a NEW createSendAgc;
// without this it would start at unity (1.0) and re-ramp from scratch every time
// → an audible level JUMP on each rebuild (a hot mic blasts for ~0.4 s before
// the AGC pulls it down; a quiet mic drops out until it ramps back up). This was
// a primary cause of the "floating / uneven levels" on the 2026-05-22 team call.
// One physical mic ⇒ one logical AGC state, so module-level is correct; a new
// graph resumes from the last adapted gain instead of resetting.
let lastMakeupGain = 1

// One-shot startup fade-in request. mediaManager.applyDenoiserMic calls
// requestSendAgcFade(ms) right before building the graph on a FRESH user mic
// enable (unmute / first join) — NOT on health-check re-applies — so the first
// ~1 s of a fresh capture publishes a silent-then-fade-in ramp. That swallows
// the capture/denoiser/AGC startup transient (the "first 1-2 s crackle on mic
// enable" users reported) before it reaches peers. Consumed (reset to 0) by the
// next createSendAgc, so a re-apply that immediately follows does NOT re-fade
// mid-speech. Tunable: mediaManager reads localStorage 'voiceMicFadeInMs'.
let pendingFadeMs = 0
export function requestSendAgcFade(ms: number): void {
  pendingFadeMs = Number.isFinite(ms) && ms > 0 ? Math.min(ms, 3000) : 0
}

// User-controlled output gain — Discord-style "mic input volume" slider.
// Applied AFTER sendAgc's makeup gain (so the AGC still levels the user to
// the common target first), then user-gain scales the final published
// level up or down per their preference. Default 0 dB (unity = sendAgc
// target governs alone). Range -20…+10 dB.
//
// Use case (2026-05-26 user request): Aleksei-class hot-mic users who want
// to manually attenuate their published level without the team having to
// turn down their peer-volume slider on every listener. Lower the userGain
// → peers hear them quieter at source.
//
// Live-tunable: setUserMicGainDb writes localStorage AND ramps every active
// graph's userGain node via setTargetAtTime (smooth 50 ms — no zipper noise).
// Persisted via localStorage 'voiceUserMicGainDb' (default 0 = unity).
const USER_GAIN_MIN_DB = -20
const USER_GAIN_MAX_DB = 10
const USER_GAIN_STORAGE_KEY = 'voiceUserMicGainDb'
let lastUserGainLin = 1.0
const activeUserGainNodes = new Set<GainNode>()

function clampDb(db: number): number {
  if (!Number.isFinite(db)) return 0
  return Math.max(USER_GAIN_MIN_DB, Math.min(USER_GAIN_MAX_DB, db))
}

function dbToLin(db: number): number {
  return Math.pow(10, db / 20)
}

// Initialise from localStorage on module load so the slider position
// persists across reloads / rejoins.
try {
  if (typeof localStorage !== 'undefined') {
    const raw = localStorage.getItem(USER_GAIN_STORAGE_KEY)
    if (raw !== null) {
      const v = parseFloat(raw)
      if (Number.isFinite(v)) lastUserGainLin = dbToLin(clampDb(v))
    }
  }
} catch { /* noop */ }

export function getUserMicGainDb(): number {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(USER_GAIN_STORAGE_KEY) : null
    if (raw === null) return 0
    const v = parseFloat(raw)
    return Number.isFinite(v) ? clampDb(v) : 0
  } catch { return 0 }
}

export function setUserMicGainDb(db: number): void {
  const clamped = clampDb(db)
  const lin = dbToLin(clamped)
  lastUserGainLin = lin
  try { localStorage.setItem(USER_GAIN_STORAGE_KEY, String(clamped)) } catch { /* noop */ }
  // Apply LIVE to every active graph. ~50 ms ramp = smooth but instant-feeling.
  for (const node of activeUserGainNodes) {
    try { node.gain.setTargetAtTime(lin, node.context.currentTime, 0.05) } catch { /* noop */ }
  }
}

export const USER_GAIN_RANGE_DB = { min: USER_GAIN_MIN_DB, max: USER_GAIN_MAX_DB }

export function createSendAgc(ctx: BaseAudioContext): SendAgc {
  const tee = ctx.createGain() // unity passthrough + fan-out point
  const makeup = ctx.createGain() // the applied leveling gain
  makeup.gain.value = lastMakeupGain // resume leveling across rebuilds (not unity)
  // Startup fade-in lives on its OWN node (not makeup) so the AGC timer's writes
  // to makeup.gain don't fight the ramp. 0 → hold silent → ramp to 1, ONLY when
  // a fresh enable requested it (pendingFadeMs); otherwise unity passthrough.
  const fade = ctx.createGain() // the published OUTPUT node
  const fadeMs = pendingFadeMs
  pendingFadeMs = 0 // consume — a re-apply that follows must NOT re-fade
  if (fadeMs > 0) {
    const t0 = ctx.currentTime
    const holdSec = (fadeMs / 1000) * 0.7 // mostly silent first — eats the transient
    const rampSec = (fadeMs / 1000) * 0.3 // then a quick smooth fade-in
    fade.gain.setValueAtTime(0, t0)
    fade.gain.setValueAtTime(0, t0 + holdSec)
    fade.gain.linearRampToValueAtTime(1, t0 + holdSec + rampSec)
  } else {
    fade.gain.value = 1
  }
  const analyser = ctx.createAnalyser()
  analyser.fftSize = 2048
  // User-controlled gain node, applied AFTER the AGC's makeup (so AGC
  // still targets the common level first, then user-gain scales the
  // final published amount). Default unity (0 dB) — overridden via
  // AudioDeviceSelector's "Mic input volume" slider. See setUserMicGainDb.
  const userGain = ctx.createGain()
  userGain.gain.value = lastUserGainLin
  activeUserGainNodes.add(userGain)
  tee.connect(analyser) // measure the PRE-makeup FLAT level (leaf; drives the gate + RMS mode)
  // K-weighting (LUFS) measurement branch — a SECOND leaf off `tee`, NEVER in the
  // published path: pre-filter (head high-shelf) → RLB high-pass → analyser, read
  // in parallel with flat RMS. Guarded to 48 kHz (#9); on failure we stay flat-only.
  let kPre: IIRFilterNode | null = null
  let kRlb: IIRFilterNode | null = null
  let kAnalyser: AnalyserNode | null = null
  if (ctx.sampleRate === 48000) {
    try {
      kPre = ctx.createIIRFilter(KW_PREFILTER_B, KW_PREFILTER_A)
      kRlb = ctx.createIIRFilter(KW_RLB_B, KW_RLB_A)
      kAnalyser = ctx.createAnalyser()
      kAnalyser.fftSize = 2048
      tee.connect(kPre)
      kPre.connect(kRlb)
      kRlb.connect(kAnalyser)
    } catch (err) {
      console.warn('[sendagc] K-weighting init failed; LUFS mode unavailable, flat RMS only:', err)
      kPre = null
      kRlb = null
      kAnalyser = null
    }
  }
  tee.connect(makeup) // apply the makeup
  makeup.connect(userGain) // → user-controlled gain slider (default unity)
  userGain.connect(fade) // → startup fade-in → published output
  const buf = new Float32Array(analyser.fftSize)
  const kBuf = kAnalyser ? new Float32Array(kAnalyser.fftSize) : null
  let logTick = 0
  const timer = setInterval(() => {
    const enabled =
      typeof localStorage === 'undefined' || localStorage.getItem('voiceSendAgc') !== 'false'
    if (!enabled) {
      // Feature off → ease makeup back to unity (no leveling).
      makeup.gain.setTargetAtTime(1, ctx.currentTime, 0.3)
      lastMakeupGain = 1
      return
    }
    // FLAT level — always measured: drives the speech gate, the legacy RMS mode,
    // and the dual log. Gate on RAW energy so weighting never affects silence detect.
    analyser.getFloatTimeDomainData(buf)
    let sum = 0
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i]
    const rms = Math.sqrt(sum / buf.length)
    if (rms < SPEECH_FLOOR) return // silence/noise — hold current gain

    // K-WEIGHTED (perceptual) level — measured in parallel when the branch built.
    let kRms = 0
    if (kAnalyser && kBuf) {
      kAnalyser.getFloatTimeDomainData(kBuf)
      let kSum = 0
      for (let i = 0; i < kBuf.length; i++) kSum += kBuf[i] * kBuf[i]
      kRms = Math.sqrt(kSum / kBuf.length)
    }

    // Choose the control signal. 'lufs' levels toward the K-weighted target;
    // default 'rms' reproduces the exact legacy behaviour. LUFS engages only if
    // the K branch is present AND we got a positive reading this tick.
    const lufsMode =
      kAnalyser !== null &&
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('voiceSendAgcMode') === 'lufs'
    let level = rms
    let target = TARGET_RMS
    if (lufsMode && kRms > 0) {
      level = kRms
      const tDb = parseFloat(localStorage.getItem('voiceSendAgcTargetDb') || '')
      target = Number.isFinite(tDb) ? Math.pow(10, tDb / 20) : TARGET_LUFS_LIN_DEFAULT
    }

    let desired = target / level
    if (desired < MIN_GAIN) desired = MIN_GAIN
    else if (desired > MAX_GAIN) desired = MAX_GAIN
    makeup.gain.setTargetAtTime(desired, ctx.currentTime, TIME_CONSTANT)
    lastMakeupGain = desired // persist so the next graph rebuild resumes here

    // Dual MEASUREMENT log every ~2 s — shows the flat-vs-K-weighted offset per
    // talker so the LUFS target is calibrated from real speech (not guessed).
    // Greppable '[sendagc]' (invariant #30 — don't downgrade to console.debug).
    if (logTick++ % 10 === 0) {
      const flatDb = (20 * Math.log10(rms)).toFixed(1)
      const kDb = kRms > 0 ? (20 * Math.log10(kRms)).toFixed(1) : 'n/a'
      console.info(
        `[sendagc] flat ${flatDb} dBFS | kweighted ${kDb} dBFS | mode=${lufsMode ? 'lufs' : 'rms'} | gain=${desired.toFixed(2)}`,
      )
    }
  }, INTERVAL_MS)
  return {
    input: tee,
    output: fade,
    destroy: () => {
      clearInterval(timer)
      activeUserGainNodes.delete(userGain)
      try { tee.disconnect() } catch { /* noop */ }
      try { makeup.disconnect() } catch { /* noop */ }
      try { userGain.disconnect() } catch { /* noop */ }
      try { fade.disconnect() } catch { /* noop */ }
      try { analyser.disconnect() } catch { /* noop */ }
      try { kPre?.disconnect() } catch { /* noop */ }
      try { kRlb?.disconnect() } catch { /* noop */ }
      try { kAnalyser?.disconnect() } catch { /* noop */ }
    },
  }
}
